import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2, Shirt, User } from 'lucide-react';
import {
  fetchBenevoleMoi,
  fetchReferentielBenevole,
  updateBenevoleMoi,
} from '../services/api';

/**
 * Une couleur par taille, de la plus petite à la plus grande.
 *
 * Le bureau ne lit pas ces pastilles une par une : il compte combien de L et
 * combien de XL avant de passer commande au fournisseur. Toutes de la même
 * couleur, il faut lire chaque libellé ; échelonnées, les paquets se voient.
 *
 * L'ordre des teintes suit celui des tailles — du bleu au rouge — pour que la
 * lecture soit la même d'une fiche à l'autre. Une taille inconnue tombe sur le
 * gris plutôt que d'être coloriée au hasard.
 */
function couleurTaille(taille: string | undefined): string {
  const echelle: Record<string, string> = {
    XS: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-200',
    S: 'border-cyan-200 bg-cyan-50 text-cyan-800 dark:border-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-200',
    M: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200',
    L: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200',
    XL: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-900/20 dark:text-orange-200',
    XXL: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200',
    '3XL': 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200',
    // Taille unique : elle ne se commande pas par paquets, d'où le violet qui
    // la sort de l'échelle plutôt que de l'y ranger.
    TU: 'border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-900/20 dark:text-violet-200',
  };

  return (
    echelle[(taille ?? '').toUpperCase()] ??
    'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
  );
}

/**
 * Fiche bénévole du membre connecté : tailles, compétences, disponibilités.
 *
 * Elle vivait sur l'écran des missions, où personne n'allait la chercher : on
 * y va pour répondre à une affectation, pas pour déclarer sa taille de polo.
 * Sa place est dans « Mon espace », avec le reste de ce que le membre dit de
 * lui — repliée par défaut, car on n'y touche qu'une fois.
 */
export default function ProfilBenevole() {
  const queryClient = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [edition, setEdition] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});

  const query = useQuery({ queryKey: ['benevole-moi'], queryFn: fetchBenevoleMoi });
  const referentiel = useQuery({
    queryKey: ['referentiel-benevole'],
    queryFn: fetchReferentielBenevole,
    // Le référentiel n'est utile qu'une fois la fiche dépliée.
    enabled: ouvert,
  });

  const enregistrer = useMutation({
    mutationFn: updateBenevoleMoi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benevole-moi'] });
      setEdition(false);
    },
  });

  const benevole = query.data?.benevole;
  const vetements = referentiel.data?.vetements ?? [];
  const competencesDispo = referentiel.data?.competences ?? [];

  const ouvrirEdition = () => {
    setForm({
      telephone: benevole?.telephone ?? '',
      adresse: benevole?.adresse ?? '',
      dateNaissance: benevole?.dateNaissance ?? '',
      tailles: benevole?.tailles ?? {},
      competences: benevole?.competences ?? [],
      disponibilites: benevole?.disponibilites ?? [],
    });
    setEdition(true);
  };

  const sauver = () =>
    enregistrer.mutate({
      ...form,
      competences: Array.isArray(form.competences) ? form.competences : [],
      disponibilites: Array.isArray(form.disponibilites)
        ? form.disponibilites
        : String(form.disponibilites)
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean),
    });

  return (
    <div className="card lg:p-6 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold flex items-center gap-2">
          <User className="w-5 h-5 text-primary-600" /> Ma fiche bénévole
        </h2>
        <div className="flex items-center gap-1 shrink-0">
          {ouvert && !edition && (
            <button onClick={ouvrirEdition} className="text-sm text-primary-700 hover:underline">
              Modifier
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setOuvert(!ouvert);
              setEdition(false);
            }}
            aria-expanded={ouvert}
            aria-label={ouvert ? 'Replier ma fiche bénévole' : 'Ouvrir ma fiche bénévole'}
            className="p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            {ouvert ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {!ouvert && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Tailles de vêtements, compétences et disponibilités.
        </p>
      )}

      {ouvert && query.isPending && (
        <div className="py-4 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
        </div>
      )}

      {ouvert && !query.isPending && !edition && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm pt-2 border-t border-gray-100 dark:border-gray-800">
          <p>
            <strong>Téléphone :</strong> {benevole?.telephone ?? '—'}
          </p>
          <p>
            <strong>Date de naissance :</strong> {benevole?.dateNaissance ?? '—'}
          </p>
          {/* Des pastilles plutôt qu'une énumération séparée par des virgules.
              « Polo L, Sweat XL, Casquette (unique) » se lit ligne par ligne ;
              trois pastilles se comptent d'un regard, et c'est ainsi qu'on
              consulte une fiche — pour vérifier ce qui est renseigné, pas pour
              la lire. */}
          <p className="lg:col-span-2 flex items-start gap-1 flex-wrap">
            <Shirt className="w-4 h-4 mt-0.5 shrink-0" />
            <strong className="mr-1">Tailles :</strong>
            {Object.keys(benevole?.tailles ?? {}).length ? (
              vetements
                .filter((v: any) => benevole?.tailles?.[v.code])
                .map((v: any) => (
                  <span
                    key={v.code}
                    className={`inline-flex items-center gap-1 text-xs rounded-full border px-2 py-0.5 ${couleurTaille(
                      benevole.tailles[v.code]
                    )}`}
                  >
                    {v.label}
                    <span className="font-semibold">
                      {benevole.tailles[v.code] === 'TU' ? 'unique' : benevole.tailles[v.code]}
                    </span>
                  </span>
                ))
            ) : (
              <span className="text-gray-500 dark:text-gray-400">—</span>
            )}
          </p>
          <p className="lg:col-span-2">
            <strong>Adresse :</strong> {benevole?.adresse ?? '—'}
          </p>
          <p className="lg:col-span-2 flex items-start gap-1 flex-wrap">
            <strong className="mr-1">Compétences :</strong>
            {benevole?.competencesLabels?.length ? (
              benevole.competencesLabels.map((c: string) => (
                <span
                  key={c}
                  className="text-xs rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 px-2 py-0.5"
                >
                  {c}
                </span>
              ))
            ) : (
              <span className="text-gray-500 dark:text-gray-400">—</span>
            )}
          </p>
          <p className="lg:col-span-2">
            <strong>Disponibilités :</strong>{' '}
            {benevole?.disponibilites?.length ? benevole.disponibilites.join(', ') : '—'}
          </p>
        </div>
      )}

      {ouvert && edition && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
          <input
            className="input"
            placeholder="Téléphone"
            value={form.telephone}
            onChange={(e) => setForm({ ...form, telephone: e.target.value })}
          />
          <input
            className="input"
            type="date"
            value={form.dateNaissance}
            onChange={(e) => setForm({ ...form, dateNaissance: e.target.value })}
          />
          <textarea
            className="input lg:col-span-2"
            placeholder="Adresse"
            value={form.adresse}
            onChange={(e) => setForm({ ...form, adresse: e.target.value })}
          />

          <div className="lg:col-span-2 space-y-2">
            <p className="text-sm font-medium text-gray-700 flex items-center gap-2 dark:text-gray-300">
              <Shirt className="w-4 h-4" /> Tailles de vêtements
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Renseignez uniquement les vêtements qui vous concernent.
            </p>
            {vetements.map((v: any) => (
              <div key={v.code} className="flex flex-wrap items-center gap-2">
                <span className="text-sm w-28 shrink-0">{v.label}</span>
                {v.tailles.map((t: string) => {
                  const choisie = form.tailles?.[v.code] === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          tailles: {
                            ...form.tailles,
                            // Un second clic retire la taille : le vêtement
                            // redevient non renseigné.
                            [v.code]: choisie ? undefined : t,
                          },
                        })
                      }
                      className={`px-3 py-1 text-sm rounded-lg border transition ${
                        choisie
                          ? 'border-primary-600 bg-primary-50 text-primary-700 font-medium'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:text-gray-400'
                      }`}
                    >
                      {t === 'TU' ? 'Taille unique' : t}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="lg:col-span-2 space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Compétences</p>
            <div className="flex flex-wrap gap-2">
              {competencesDispo.map((comp: any) => {
                const cochee = (form.competences ?? []).includes(comp.code);
                return (
                  <button
                    key={comp.code}
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        competences: cochee
                          ? form.competences.filter((c: string) => c !== comp.code)
                          : [...form.competences, comp.code],
                      })
                    }
                    className={`px-3 py-1.5 text-sm rounded-full border transition ${
                      cochee
                        ? 'border-primary-600 bg-primary-50 text-primary-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:text-gray-400'
                    }`}
                  >
                    {comp.label}
                  </button>
                );
              })}
            </div>
          </div>

          <textarea
            className="input lg:col-span-2"
            placeholder="Disponibilités (séparées par virgule)"
            value={
              Array.isArray(form.disponibilites)
                ? form.disponibilites.join(', ')
                : form.disponibilites
            }
            onChange={(e) => setForm({ ...form, disponibilites: e.target.value })}
          />

          <div className="lg:col-span-2 flex flex-wrap gap-2">
            <button
              onClick={sauver}
              disabled={enregistrer.isPending}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {enregistrer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
            <button
              onClick={() => setEdition(false)}
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
